// P5.4 (2026-11-03) · Item 9 — Acceptance dry-run matrix page.
//
// Owner-only. Pick a feed set + (optional) reel/carousel sets, click
// Run, and see a pass/fail grid for every (channel, surface) pair in
// the acceptance matrix. All plans are test_mode so no real HTTP is
// issued, and the backend deletes them on exit. The output tells
// Roger which surfaces to actually smoke before any real posting.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { channelDisplay, fmtDateTimeTz, useOrgTimezone } from "../lib/tz";

const STATUS_COLOR = {
  RUNNING: "var(--bo-accent, #2f5d8a)",
  PASS:    "var(--bo-success, #0a7a3b)",
  WARN:    "var(--bo-warn, #a05a00)",
  BLOCK:   "var(--bo-danger, #b1261a)",
  ERRORED: "var(--bo-danger, #b1261a)",
  SKIPPED: "var(--bo-muted)",
  // Lower-case keys preserved for the legacy preflight_status column.
  errored: "var(--bo-danger, #b1261a)",
  skipped: "var(--bo-muted)",
  unknown: "var(--bo-muted)",
};

export default function AcceptanceDryRun() {
  const tz = useOrgTimezone();
  const [sets, setSets] = useState([]);
  const [feedId, setFeedId] = useState("");
  const [reelId, setReelId] = useState("");
  const [carouselId, setCarouselId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/asset-sets/publishable?limit=200");
      setSets(r.data?.asset_sets || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const feedSets = sets.filter((s) => (s.kind || "feed") === "feed");
  const reelSets = sets.filter((s) => s.kind === "reel");
  const carouselSets = sets.filter((s) => s.kind === "carousel");

  async function run() {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post("/publish/acceptance/dry-run", {
        asset_set_id:    feedId,
        reel_set_id:     reelId || undefined,
        carousel_set_id: carouselId || undefined,
      });
      setResult(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Acceptance Dry-Run">
      <div className="bo-page" data-testid="acceptance-dry-run-page"
           style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start",
                       marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div className="bo-eyebrow">Acceptance</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: "4px 0" }}>
              Dry-run matrix
            </h1>
            <p style={{ color: "var(--bo-muted)", maxWidth: 720,
                        lineHeight: 1.55 }}>
              Pick a feed set (image + copy), a frozen reel set (video + caption)
              and, optionally, a carousel set. We'll create a <strong>test-mode</strong> plan
              for every (channel, surface) pair, run preflight, run the
              test-mode actuation (no real HTTP), and show the grid. Plans
              are deleted on exit. Run this before any real test posts.
            </p>
          </div>
          <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>← Queue</Link>
        </div>

        {error && (
          <div className="bo-card" style={{ padding: 10, marginBottom: 12,
            color: "var(--bo-danger)" }} data-testid="dry-run-error">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        <div className="bo-card" style={{ padding: 18, display: "grid",
          gap: 12, marginBottom: 16 }}
          data-testid="dry-run-controls">
          <div style={{ display: "grid", gridTemplateColumns:
            "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <SetPicker label="Feed set (image + copy)" value={feedId}
              onChange={setFeedId} options={feedSets} required testId="dry-run-feed-set" />
            <SetPicker label="Reel set (video + caption)" value={reelId}
              onChange={setReelId} options={reelSets} testId="dry-run-reel-set" />
            <SetPicker label="Carousel set (optional)" value={carouselId}
              onChange={setCarouselId} options={carouselSets}
              testId="dry-run-carousel-set" />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="dry-run-submit"
              onClick={run}
              disabled={busy || !feedId}
            >
              {busy ? "Running…" : "Run acceptance matrix"}
            </button>
          </div>
        </div>

        {result && (
          <div data-testid="dry-run-result">
            <div className="bo-card" style={{ padding: 14, marginBottom: 12 }}
              data-testid="dry-run-summary">
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span><strong data-testid="dry-run-summary-passed">
                  {result.summary.passed}</strong> passed</span>
                <span><strong>{result.summary.warned}</strong> warned</span>
                <span><strong>{result.summary.blocked}</strong> blocked</span>
                <span><strong>{result.summary.errored}</strong> errored</span>
                <span><strong>{result.summary.skipped}</strong> skipped</span>
                <span style={{ color: "var(--bo-muted)", marginLeft: "auto" }}>
                  {fmtDateTimeTz(result.ran_at, tz)} · {result.elapsed_ms}ms ·
                  cleaned up {result.cleaned_up_plans}/{result.created_plan_count} plans
                </span>
              </div>
            </div>
            <div className="bo-card" style={{ padding: 0, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse",
                              fontSize: 13 }} data-testid="dry-run-grid">
                <thead>
                  <tr style={{ background: "var(--bo-panel-alt)" }}>
                    <th style={th}>Channel</th>
                    <th style={th}>Surface</th>
                    <th style={th}>Verdict</th>
                    <th style={th}>Preflight</th>
                    <th style={th}>Failing step</th>
                    <th style={th}>Actuation</th>
                    <th style={th}>Reason / Receipt</th>
                    <th style={th}>Live</th>
                  </tr>
                </thead>
                <tbody>
                  {result.cells.map((c, i) => (
                    <LiveCellRow
                      key={i}
                      cell={c}
                      assetSetId={feedId}
                      reelId={reelId}
                      carouselId={carouselId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

const th = { padding: "8px 10px", textAlign: "left", fontSize: 12,
             fontWeight: 600, color: "var(--bo-muted)",
             textTransform: "uppercase", letterSpacing: "0.04em" };
const td = { padding: "8px 10px", verticalAlign: "top" };

function SetPicker({ label, value, onChange, options, required, testId }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <label className="bo-label">{label}{required ? " *" : ""}</label>
      <select
        className="bo-input"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— pick —</option>
        {options.map((s) => (
          <option key={s.id} value={s.id}>
            {(s.name || s.id).slice(0, 60)}
          </option>
        ))}
      </select>
    </div>
  );
}


// P5.8 — one row of the matrix, with inline "Run live" + "Delete".
// publish-85 round-9 · item A — "Run live" now calls the ASYNC endpoint
// (202 + receipt_id) and polls GET /publish/acceptance/live-run/{id}
// every 3s, updating this row in place until the receipt is terminal.
const LIVE_POLL_MS = 3000;
const LIVE_POLL_MAX_MS = 15 * 60 * 1000;

const RUNNING_LABEL = {
  live_run_pending: "Starting…",
  publishing:       "Publishing…",
  pending_platform: "Platform processing…",
};

function liveFromStatus(st) {
  // Merge the polled status row into the shape LiveCellRow renders.
  const out = st.live_run_out || {};
  const fe = st.failure_envelope || null;
  const terminal = !!st.done;
  const status = st.execution_status || st.state;
  let verdict = out.verdict;
  if (!verdict) {
    if (!terminal) verdict = "RUNNING";
    else if (status === "published") verdict = "PASS";
    else if (status === "pending_platform") verdict = "WARN";
    else if (status === "live_run_skipped") verdict = "SKIPPED";
    else if (status === "live_run_cancelled" || status === "live_run_errored") verdict = "ERRORED";
    else verdict = "BLOCK";
  }
  return {
    ...out,
    verdict,
    running: !terminal,
    execution_status: status,
    receipt_id: st.receipt_id,
    plan_id: out.plan_id || st.plan_id,
    external_artifact_id: st.external_artifact_id || out.external_artifact_id,
    external_artifact_url: st.external_artifact_url || out.external_artifact_url,
    provider_ids: st.provider_ids || {},
    failure_envelope: fe,
    reason: out.reason || (fe ? `${fe.code || "error"}${fe.message ? ": " + fe.message : ""}` : undefined),
    block: out.block || (fe && terminal ? { step: "actuation", code: fe.code, detail: fe } : undefined),
    actuation_status: out.actuation_status
      || (status === "published" ? "ok"
        : status === "failed" ? "failed"
        : terminal ? undefined : "running"),
    delete_supported: out.delete_supported
      ?? (!!(st.external_artifact_id || out.external_artifact_id)),
  };
}

function LiveCellRow({ cell: c, assetSetId, reelId, carouselId }) {
  const [live, setLive] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [deleteState, setDeleteState] = React.useState(null);
  const pollRef = React.useRef({ timer: null, alive: true });
  const canLive = !!assetSetId && c.verdict !== "SKIPPED";
  const cellId = `${c.channel}:${c.surface}`;

  React.useEffect(() => {
    const ref = pollRef.current;
    ref.alive = true;
    return () => {
      ref.alive = false;
      if (ref.timer) clearTimeout(ref.timer);
    };
  }, []);

  async function pollUntilDone(receiptId, startedAt) {
    const ref = pollRef.current;
    if (!ref.alive) return;
    try {
      const r = await api.get(`/publish/acceptance/live-run/${receiptId}`);
      const st = r.data || {};
      if (!ref.alive) return;
      if (st.state === "not_found") {
        setLive({ verdict: "ERRORED", running: false, receipt_id: receiptId,
                  reason: "live-run receipt disappeared" });
        setBusy(false);
        return;
      }
      setLive(liveFromStatus(st));
      if (st.done) { setBusy(false); return; }
      if (Date.now() - startedAt > LIVE_POLL_MAX_MS) {
        setLive((prev) => ({ ...(prev || {}), running: false, verdict: "WARN",
          reason: "Still running after 15 min — check the Health page; the background poller will finish it." }));
        setBusy(false);
        return;
      }
    } catch (e) {
      // Transient poll error: keep polling, but surface it.
      if (!ref.alive) return;
      setLive((prev) => ({ ...(prev || {}), verdict: "RUNNING", running: true,
        receipt_id: receiptId, poll_error: formatApiError(e) || e.message }));
    }
    ref.timer = setTimeout(() => pollUntilDone(receiptId, startedAt), LIVE_POLL_MS);
  }

  async function runLive() {
    if (!window.confirm(
      `Publish ${cellId} FOR REAL (test_mode:false)? ` +
      `This writes to the live platform and tags the receipt ` +
      `as an acceptance test.`,
    )) return;
    setBusy(true);
    setLive({ verdict: "RUNNING", running: true, execution_status: "live_run_pending" });
    setDeleteState(null);
    try {
      const r = await api.post("/publish/acceptance/live-run/async", {
        cell: cellId,
        asset_set_id: assetSetId,
        reel_set_id: reelId || null,
        carousel_set_id: carouselId || null,
      });
      const receiptId = r.data?.receipt_id;
      if (!receiptId) {
        setLive({ verdict: "ERRORED", running: false,
                  reason: "async live-run returned no receipt_id" });
        setBusy(false);
        return;
      }
      setLive({ verdict: "RUNNING", running: true, receipt_id: receiptId,
                execution_status: r.data?.state || "live_run_pending" });
      pollUntilDone(receiptId, Date.now());
    } catch (e) {
      setLive({ verdict: "ERRORED", running: false,
                reason: formatApiError(e) || e.message });
      setBusy(false);
    }
  }
  async function deleteNow() {
    const rid = live?.receipt_id;
    if (!rid) return;
    if (!window.confirm("Delete this real post from the platform?")) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/publish-receipts/${rid}/delete-from-platform`,
      );
      setDeleteState(r.data || { status: "unknown" });
    } catch (e) {
      setDeleteState({ status: "failed",
                        reason: formatApiError(e) || e.message });
    } finally {
      setBusy(false);
    }
  }

  const shown = live || c;
  return (
    <tr data-testid={`dry-run-cell-${c.channel}-${c.surface}`}
        style={{ borderTop: "1px solid var(--bo-line)" }}>
      <td style={td}>{channelDisplay(c.channel)}</td>
      <td style={{ ...td, fontFamily: "var(--bo-mono)" }}>{c.surface}</td>
      <td
        style={{ ...td,
          color: STATUS_COLOR[shown.verdict] || STATUS_COLOR.unknown,
          fontWeight: 700 }}
        data-testid={`dry-run-cell-verdict-${c.channel}-${c.surface}`}
      >
        {shown.verdict || "—"}
        {live?.running && (
          <div data-testid={`live-run-progress-${c.channel}-${c.surface}`}
               style={{ fontSize: 11, fontWeight: 500,
                        color: "var(--bo-muted)", marginTop: 2 }}>
            {RUNNING_LABEL[live.execution_status] || live.execution_status || "Working…"}
            {live.poll_error ? " · retrying poll" : ""}
          </div>
        )}
      </td>
      <td style={{ ...td,
        color: STATUS_COLOR[shown.preflight_status] || STATUS_COLOR.unknown,
        fontWeight: 600 }}>
        {shown.preflight_status || "—"}
      </td>
      <td
        style={{ ...td, fontFamily: "var(--bo-mono)",
                  fontSize: 12, maxWidth: 220 }}
        data-testid={`dry-run-cell-step-${c.channel}-${c.surface}`}
        title={
          shown.block
            ? `${shown.block.step || ""}${shown.block.code ? " · " + shown.block.code : ""}` +
              (shown.block.detail
                ? "\n\n" + JSON.stringify(shown.block.detail, null, 2)
                : "")
            : ""
        }
      >
        {shown.block ? (
          <>
            <div style={{ fontWeight: 600 }}>{shown.block.step}</div>
            {shown.block.code && (
              <div style={{ color: "var(--bo-muted)" }}>{shown.block.code}</div>
            )}
          </>
        ) : (
          <span style={{ color: "var(--bo-muted)" }}>—</span>
        )}
      </td>
      <td style={{ ...td,
        color: shown.actuation_status === "ok"
               ? STATUS_COLOR.PASS
               : shown.actuation_status === "failed"
               ? STATUS_COLOR.errored
               : STATUS_COLOR.skipped }}>
        {shown.actuation_status || "—"}
      </td>
      <td style={{ ...td, fontFamily: "var(--bo-mono)",
                    fontSize: 12 }}>
        {live?.external_artifact_url
          ? <a href={live.external_artifact_url} target="_blank"
               rel="noreferrer"
               data-testid={`live-permalink-${c.channel}-${c.surface}`}>
              {live.external_artifact_url.slice(0, 48)}…
            </a>
          : (shown.receipt_external_id || shown.reason || "")}
        {live?.provider_ids && Object.keys(live.provider_ids).length > 0 && !live.external_artifact_url && (
          <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 4 }}
               data-testid={`live-provider-ids-${c.channel}-${c.surface}`}>
            {Object.entries(live.provider_ids).map(([k, v]) => `${k}=${v}`).join(" · ")}
          </div>
        )}
        {live?.receipt_id && (
          <div style={{ fontSize: 11, marginTop: 4 }}>
            <Link to={`/brand-os/publish/receipts/${live.receipt_id}`}
                  data-testid={`live-receipt-link-${c.channel}-${c.surface}`}>
              receipt {live.receipt_id.slice(0, 8)}
            </Link>
            {(live.execution_status === "pending_platform" || live.verdict === "WARN") && (
              <> · <Link to="/brand-os/publish/health"
                        data-testid={`live-health-link-${c.channel}-${c.surface}`}>Health</Link></>
            )}
          </div>
        )}
        {live?.manual_help && (live.verdict !== "PASS" || live.delete_supported === false) && (
          <div style={{ fontSize: 11, color: "var(--bo-muted)",
                         marginTop: 4 }}
               data-testid={`live-manual-help-${c.channel}-${c.surface}`}>
            {live.manual_help}
          </div>
        )}
        {deleteState && (
          <div style={{ fontSize: 11, marginTop: 4,
                         color: deleteState.status === "deleted"
                                ? STATUS_COLOR.PASS : STATUS_COLOR.errored }}>
            Delete: {deleteState.status || "unknown"}
            {deleteState.manual_help && (
              <div style={{ color: "var(--bo-muted)" }}>
                {deleteState.manual_help}
              </div>
            )}
          </div>
        )}
      </td>
      <td style={{ ...td }}>
        <button
          type="button"
          className="bo-btn bo-btn-danger"
          data-testid={`live-run-btn-${c.channel}-${c.surface}`}
          onClick={runLive}
          disabled={!canLive || busy}
          style={{ padding: "4px 10px", fontSize: 11 }}
          title={canLive
            ? "Publish for real as an acceptance test"
            : "Pick a feed set first (and reel/carousel if the cell needs one)"}
        >
          {busy ? (live?.running ? "Running…" : "…") : "Run live"}
        </button>
        {live?.receipt_id && !live?.running && live?.delete_supported && !deleteState && (
          <button
            type="button"
            className="bo-btn bo-btn-ghost"
            data-testid={`live-delete-btn-${c.channel}-${c.surface}`}
            onClick={deleteNow}
            disabled={busy}
            style={{ padding: "4px 10px", fontSize: 11,
                      marginTop: 4, display: "block" }}
          >
            Delete from platform
          </button>
        )}
      </td>
    </tr>
  );
}
